import React from "react"
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import styled from "@emotion/styled";
import { useTheme } from "@mui/styles";
import FormControlLabel from '@mui/material/FormControlLabel';
import MortgageDropdown from "../../../../../components/DropDowns/MortgageDropdown";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const FinancialsControls = ({ financialsPresent, displaySelection, handleDisplayChange, handleSetPaymentsToView, selectedMortgage, teamMortgages, mortgageFinancials, handleOpenAddPaymentWindow, timelineSelection, handleTimelineChange, teamPortfolioStatsOneYear }) => {
  const theme = useTheme()

  const radioSx = {
    padding: "0",
    color: theme.palette.common.grayBorderDark,
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
  }

  const timelineControlProps = (item) => ({
    checked: timelineSelection === item,
    onChange: handleTimelineChange,
    value: item,
    label: item,
    labelPlacement: "start",
  })

  const displayControlProps = (item) => ({
    checked: displaySelection === item,
    onChange: handleDisplayChange,
    value: item,
    label: item,
    labelPlacement: "start",
  })

  const formatCurrency = (value) => (
    value.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})
  )

  const monthlyStats = teamPortfolioStatsOneYear && teamPortfolioStatsOneYear[11] ? teamPortfolioStatsOneYear[11] : null

  return (
    <ControlsSurface>
      <ControlsHeader>
        <div>
          <SectionEyebrow>Financial Controls</SectionEyebrow>
          <SectionTitle>Portfolio View</SectionTitle>
        </div>
        <SectionSummary>Select the financial lens, timeline, or mortgage payment history to analyze.</SectionSummary>
      </ControlsHeader>

      <ControlsBody>
        <ControlGroup>
          <ControlLabel>Display</ControlLabel>
          <SegmentGroup>
            <FormControl>
              <RadioGroup row>
                <SegmentOption
                  control={<Radio sx={radioSx} />}
                  {...displayControlProps('Portfolio Overview')} 
                />
                <SegmentOption
                  control={<Radio sx={radioSx} />}
                  {...displayControlProps('Mortgage Payments')} 
                />
              </RadioGroup>
            </FormControl>
          </SegmentGroup>
        </ControlGroup>

        {displaySelection === 'Portfolio Overview' ?
          <>
            <ControlGroup>
              <ControlLabel>Timeframe</ControlLabel>
              <SegmentGroup className="compact">
                <FormControl>
                  <RadioGroup row>
                    <SegmentOption
                      control={<Radio sx={radioSx} />}
                      {...timelineControlProps('All')} 
                    />
                    <SegmentOption
                      control={<Radio sx={radioSx} />}
                      {...timelineControlProps('1 Year')} 
                    />
                    <SegmentOption
                      control={<Radio sx={radioSx} />}
                      {...timelineControlProps('2 Year')} 
                    />
                  </RadioGroup>
                </FormControl>
              </SegmentGroup>
            </ControlGroup>

            {monthlyStats &&
              <MetricStrip>
                <MetricCard>
                  <MetricLabel>Payments received</MetricLabel>
                  <MetricValue><Accent>$</Accent>{formatCurrency(monthlyStats.totalPaymentsReceived)}</MetricValue>
                </MetricCard>
                <MetricCard className="wide">
                  <MetricLabel>Outstanding balance</MetricLabel>
                  <MetricValue>
                    <Accent>$</Accent>{formatCurrency(monthlyStats.totalInterestRemaining+monthlyStats.totalPrincipalRemaining)}
                    <MetricPercent>
                      ({(((monthlyStats.totalInterestRemaining+monthlyStats.totalPrincipalRemaining)/((monthlyStats.totalOriginalInterest+monthlyStats.totalOriginalLoanAmount)))*100).toLocaleString("en-US", {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                    </MetricPercent>
                  </MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Interest received</MetricLabel>
                  <MetricValue><Accent>$</Accent>{formatCurrency(monthlyStats.totalInterestReceived)}</MetricValue>
                </MetricCard>
                <MetricCard className="wide">
                  <MetricLabel>Outstanding interest</MetricLabel>
                  <MetricValue>
                    <Accent>$</Accent>{formatCurrency(monthlyStats.totalInterestRemaining)}
                    <MetricPercent>
                      ({((monthlyStats.totalInterestRemaining/monthlyStats.totalOriginalInterest)*100).toLocaleString("en-US", {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                    </MetricPercent>
                  </MetricValue>
                </MetricCard>
              </MetricStrip>
            }
          </>
        :
          <>
            <MortgageSelector>
              <ControlLabel>Mortgage</ControlLabel>
              <MortgageDropdown
                value={selectedMortgage}
                handleChange={handleSetPaymentsToView}
                items={teamMortgages}
                origin={'financials'}
              />
            </MortgageSelector>

            {mortgageFinancials &&
              <MetricStrip className="mortgage">
                <MetricCard>
                  <MetricLabel>Original principal</MetricLabel>
                  <MetricValue><Accent>$</Accent>{formatCurrency(mortgageFinancials.originalLoanAmount)}</MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Original interest</MetricLabel>
                  <MetricValue><Accent>$</Accent>{formatCurrency(mortgageFinancials.originalInterestDue)}</MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Monthly payments</MetricLabel>
                  <MetricValue><Accent>$</Accent>{formatCurrency(mortgageFinancials.monthlyPayments)}</MetricValue>
                </MetricCard>
                <MetricCard className="wide">
                  <MetricLabel>Principal remaining</MetricLabel>
                  <MetricValue>
                    <Accent>$</Accent>{formatCurrency(mortgageFinancials.principalRemaining)}
                    <MetricPercent>
                      ({((mortgageFinancials.principalRemaining/mortgageFinancials.originalLoanAmount)*100).toLocaleString("en-US", {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                    </MetricPercent>
                  </MetricValue>
                </MetricCard>
                <MetricCard className="wide">
                  <MetricLabel>Interest remaining</MetricLabel>
                  <MetricValue>
                    <Accent>$</Accent>{formatCurrency(mortgageFinancials.interestRemaining)}
                    <MetricPercent>
                      ({((mortgageFinancials.interestRemaining/mortgageFinancials.originalInterestDue)*100).toLocaleString("en-US", {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                    </MetricPercent>
                  </MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Remaining term</MetricLabel>
                  <MetricValue className="small">{mortgageFinancials.remainingTerm}</MetricValue>
                </MetricCard>
              </MetricStrip>
            }

            <PaymentAction
              disabled={!financialsPresent}
              onClick={financialsPresent ? () => handleOpenAddPaymentWindow() : undefined}
              type="button"
            >
              <AddCircleOutlineIcon />
              Add a new payment
            </PaymentAction>
          </>
        }
      </ControlsBody>
    </ControlsSurface>
  )
}

const ControlsSurface = styled.section(({ theme }) => `
  min-width: 0;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const ControlsHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: end;
  padding: 0.58rem 0.75rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const SectionEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.1rem;
  color: ${theme.palette.primary.main};
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const SectionTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.94rem;
  font-weight: 850;
  line-height: 1.2;
`)

const SectionSummary = styled.p(({ theme }) => `
  max-width: 32rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.72rem;
  font-weight: 650;
  line-height: 1.25;
  text-align: right;
`)

const ControlsBody = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem;
  padding: 0.58rem 0.65rem;
  overflow: hidden;
`

const ControlGroup = styled.div`
  min-width: max-content;
  display: grid;
  gap: 0.24rem;
`

const ControlLabel = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const SegmentGroup = styled.div(({ theme }) => `
  min-height: 2.25rem;
  display: inline-flex;
  align-items: center;
  padding: 0.15rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.6rem;
  background: ${theme.palette.common.cardBackground};

  &.compact {
    min-width: max-content;
  }
`)

const SegmentOption = styled(FormControlLabel)(({ theme }) => `
  min-height: 1.85rem;
  margin: 0;
  gap: 0.22rem;
  padding: 0 0.48rem 0 0.38rem;
  border-radius: 0.45rem;
  color: ${theme.palette.common.grayText};

  &:has(.Mui-checked) {
    background: ${theme.palette.common.white};
    box-shadow: 0 0.12rem 0.35rem rgba(15, 23, 42, 0.08);
  }

  .MuiFormControlLabel-label {
    color: ${theme.palette.common.black};
    font-size: 0.76rem;
    font-weight: 800;
    white-space: nowrap;
  }
`)

const MetricStrip = styled.div`
  min-width: 0;
  flex: 1 1 40rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
  gap: 0.45rem;
  height: 3.5rem;

  &.mortgage {
    grid-template-columns: repeat(auto-fit, minmax(7.25rem, 1fr));
  }
`

const MetricCard = styled.article(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0.15rem;
  padding: 0.42rem 0.5rem;
  border: 0.1rem solid ${theme.palette.common.grayBorderLight};
  border-radius: 0.55rem;
  background: ${theme.palette.common.cardBackground};

  &.wide {
    min-width: 10rem;
  }
`)

const MetricLabel = styled.div(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.63rem;
  font-weight: 800;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`)

const MetricValue = styled.div(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.black};
  font-size: 1.16rem;
  font-weight: 850;
  line-height: 1.08;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  &.small {
    font-size: 0.86rem;
  }
`)

const Accent = styled.span(({ theme }) => `
  color: ${theme.palette.primary.pastelMedium};
  font-size: 1.12rem;
  font-weight: 850;
  margin-right: 0.08rem;
`)

const MetricPercent = styled.span(({ theme }) => `
  margin-left: 0.22rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 750;
`)

const MortgageSelector = styled.div`
  min-width: 20rem;
  max-width: 34rem;
  margin: 0 1rem;

  > .MuiFormControl-root {
    width: 100% !important;
    margin: 0 !important;
  }
`

const PaymentAction = styled.button(({ theme }) => `
  height: 2.3rem;
  min-width: max-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.38rem;
  padding: 0 0.72rem;
  border: 0.1rem solid ${theme.palette.primary.main};
  border-radius: 0.55rem;
  background: ${theme.palette.primary.main};
  color: ${theme.palette.common.white};
  font-size: 0.78rem;
  font-weight: 850;
  cursor: pointer;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:disabled {
    cursor: not-allowed;
    border-color: ${theme.palette.common.grayBorder};
    background: ${theme.palette.common.grayBorderLight};
    color: ${theme.palette.common.grayText};
  }
`)

export default FinancialsControls
